<script lang="ts">
	import { onMount } from 'svelte';
	import { readSettings } from '$lib/settings';

	let { size = 64, showBeta = false, themeOverride = undefined }: { size?: number; showBeta?: boolean; themeOverride?: string } = $props();

	let liveTheme = $state('hearth');
	let currentTheme = $derived(themeOverride ?? liveTheme);

	const THEME_VARS: Record<string, string> = {
		hearth: "--logo-frame-outer:#3a1400;--logo-frame-highlight:#e8a840;--logo-frame-shadow:#5a2800;--logo-frame-fill:#7a3c10;--logo-frame-text:#f5d898;--font-sans:'Pixel Operator',monospace;",
		galactic: "--logo-frame-outer:#000000;--logo-frame-highlight:#ffe81f;--logo-frame-shadow:#000000;--logo-frame-fill:#000000;--logo-frame-text:#ffe81f;--font-sans:'Cinzel',serif;",
		wizardry: "--logo-frame-outer:#05080f;--logo-frame-highlight:#e8c060;--logo-frame-shadow:#0d1228;--logo-frame-fill:#1e2860;--logo-frame-text:#c8a84b;--font-sans:'Cinzel',serif;",
		overgrown: "--logo-frame-outer:#000000;--logo-frame-highlight:#555555;--logo-frame-shadow:#111111;--logo-frame-fill:#1a1a1a;--logo-frame-text:#cccccc;--font-sans:'Oswald',sans-serif;",
		outbreak: "--logo-frame-outer:#000000;--logo-frame-highlight:#aaaaaa;--logo-frame-shadow:#111111;--logo-frame-fill:#000000;--logo-frame-text:#cccccc;--font-sans:'Bebas Neue',sans-serif;",
		catchers: "--logo-frame-outer:#2a1e00;--logo-frame-highlight:#ffde00;--logo-frame-shadow:#0e1230;--logo-frame-fill:#3b4cca;--logo-frame-text:#ffde00;--font-sans:'Fredoka',sans-serif;",
		noir: "--logo-frame-outer:#0a0a0a;--logo-frame-highlight:#444444;--logo-frame-shadow:#0a0a0a;--logo-frame-fill:#b8b8b8;--logo-frame-text:#0a0a0a;--font-sans:'Merriweather',Georgia,serif;",
		dark: "--logo-frame-outer:#ffffff;--logo-frame-highlight:#ffffff;--logo-frame-shadow:#ffffff;--logo-frame-fill:#ffffff;--logo-frame-text:#000000;--font-sans:'Merriweather',serif;",
		light: "--logo-frame-outer:#000000;--logo-frame-highlight:#000000;--logo-frame-shadow:#000000;--logo-frame-fill:#000000;--logo-frame-text:#000000;--font-sans:'Merriweather',serif;"
	};
	let overrideVars = $derived(themeOverride ? THEME_VARS[themeOverride] ?? '' : '');

	onMount(() => {
		if (themeOverride) return;
		liveTheme = readSettings().theme;
		const onStorage = () => { liveTheme = readSettings().theme; };
		window.addEventListener('storage', onStorage);
		return () => window.removeEventListener('storage', onStorage);
	});

	const grassHeightRatios = [15,21,12,18,25,11,17,23,14,20,16,26,12,18,22,15,11,21,17,24];
	const grassColors = ['#448833','#66bb44','#338822','#55aa33','#66bb44','#3a7a28','#66bb44','#448833'];
	const deadGrassColors = ['#6b5a3a','#8a7050','#4a3a22','#7a6040','#5a4a2a','#9a8060','#3a2a14','#6a5838'];
	const grassSkews  = [-1, 1, -2, 0, 2, -1, 3, 1, -3, 0, 2, -1];
	// Generate enough blades to cover the full icon width (avg ~2px each)
	const bladeCount = $derived(Math.ceil(size / 1.8));
	const grass = $derived(Array.from({ length: bladeCount }, (_, i) => ({
		w: i % 5 === 0 ? 1 : 2,
		h: Math.max(1, Math.round(grassHeightRatios[i % grassHeightRatios.length] * size / 64)),
		color: currentTheme === 'overgrown' ? deadGrassColors[i % deadGrassColors.length] : grassColors[i % grassColors.length],
		skew: grassSkews[i % grassSkews.length],
		waveDur: 1.8 + (i % 7) * 0.15,
		waveDelay: -((i * 0.08) % 2.5),
	})));

</script>

<div data-theme={themeOverride ?? undefined} style="display: inline-flex; position: relative; {overrideVars}">
<div class="logo-icon relative shrink-0 logo-icon-{currentTheme}" style="overflow: visible; display: inline-flex; width: {size}px; height: {size}px;">
	{#if currentTheme === 'hearth'}
	<div class="pointer-events-none absolute inset-0 z-10 overflow-hidden">
		<div class="pixel-frame-flat-top absolute left-0 right-0 flex items-end overflow-hidden" style="bottom: 0px;">
			{#each grass as b}
				<div class="shrink-0" style="width: {b.w}px; height: {b.h}px; background: {b.color}; transform: skewX({b.skew}deg); transform-origin: bottom center; image-rendering: pixelated;"></div>
			{/each}
		</div>
	</div>
	{:else if currentTheme === 'overgrown'}
	<div class="pointer-events-none absolute inset-0 z-10 overflow-hidden" style="border-radius: var(--frame-radius);">
	<!-- crack lines on frame corners/edges -->
	<div class="pointer-events-none absolute inset-0 z-20 overflow-hidden" style="border-radius: var(--frame-radius);">
		<!-- top-left corner crack -->
		<div style="position: absolute; top: 3px; left: 3px; width: 22px; height: 1px; background: rgba(255,255,255,0.22); transform: rotate(28deg); transform-origin: left center;"></div>
		<div style="position: absolute; top: 7px; left: 3px; width: 14px; height: 1px; background: rgba(255,255,255,0.14); transform: rotate(-15deg); transform-origin: left center;"></div>
		<div style="position: absolute; top: 5px; left: 12px; width: 10px; height: 1px; background: rgba(255,255,255,0.10); transform: rotate(55deg); transform-origin: left center;"></div>
		<!-- top-right corner crack -->
		<div style="position: absolute; top: 3px; right: 3px; width: 20px; height: 1px; background: rgba(255,255,255,0.18); transform: rotate(-22deg); transform-origin: right center;"></div>
		<div style="position: absolute; top: 6px; right: 5px; width: 12px; height: 1px; background: rgba(255,255,255,0.11); transform: rotate(40deg); transform-origin: right center;"></div>
		<!-- bottom-right crack -->
		<div style="position: absolute; bottom: 22px; right: 3px; width: 18px; height: 1px; background: rgba(255,255,255,0.16); transform: rotate(18deg); transform-origin: right center;"></div>
		<div style="position: absolute; bottom: 25px; right: 8px; width: 10px; height: 1px; background: rgba(255,255,255,0.10); transform: rotate(-35deg); transform-origin: right center;"></div>
		<!-- mid-left crack -->
		<div style="position: absolute; top: 45%; left: 2px; width: 16px; height: 1px; background: rgba(255,255,255,0.13); transform: rotate(-20deg); transform-origin: left center;"></div>
		<div style="position: absolute; top: 48%; left: 8px; width: 9px; height: 1px; background: rgba(255,255,255,0.08); transform: rotate(42deg); transform-origin: left center;"></div>
	</div>
	<!-- scratch marks on frame -->
	<div class="pointer-events-none absolute inset-0 z-20 overflow-hidden" style="border-radius: var(--frame-radius);">
		<div style="position: absolute; top: 6px; left: 8px; width: 18px; height: 1px; background: rgba(255,255,255,0.12); transform: rotate(-38deg); transform-origin: left center;"></div>
		<div style="position: absolute; top: 8px; left: 10px; width: 10px; height: 1px; background: rgba(255,255,255,0.07); transform: rotate(-38deg); transform-origin: left center;"></div>
		<div style="position: absolute; top: 4px; right: 10px; width: 14px; height: 1px; background: rgba(255,255,255,0.10); transform: rotate(25deg); transform-origin: left center;"></div>
		<div style="position: absolute; bottom: 18px; right: 6px; width: 16px; height: 1px; background: rgba(255,255,255,0.09); transform: rotate(-20deg); transform-origin: left center;"></div>
		<div style="position: absolute; bottom: 20px; right: 8px; width: 8px; height: 1px; background: rgba(255,255,255,0.06); transform: rotate(-20deg); transform-origin: left center;"></div>
		<div style="position: absolute; top: 50%; left: 5px; width: 12px; height: 1px; background: rgba(255,255,255,0.08); transform: rotate(15deg); transform-origin: left center;"></div>
	</div>
	<!-- spore dots inside frame -->
	<div class="pointer-events-none absolute z-5 overflow-hidden" style="inset: 6px; border-radius: calc(var(--frame-radius) / 2);">
		<div style="position: absolute; top: 18%; left: 12%; width: 2px; height: 2px; border-radius: 50%; background: rgba(180,200,160,0.25);"></div>
		<div style="position: absolute; top: 22%; left: 20%; width: 1px; height: 1px; border-radius: 50%; background: rgba(180,200,160,0.18);"></div>
		<div style="position: absolute; top: 15%; right: 18%; width: 2px; height: 2px; border-radius: 50%; background: rgba(180,200,160,0.20);"></div>
		<div style="position: absolute; top: 30%; right: 12%; width: 1px; height: 1px; border-radius: 50%; background: rgba(180,200,160,0.15);"></div>
		<div style="position: absolute; top: 10%; left: 40%; width: 2px; height: 2px; border-radius: 50%; background: rgba(180,200,160,0.22);"></div>
		<div style="position: absolute; top: 25%; left: 55%; width: 1px; height: 1px; border-radius: 50%; background: rgba(180,200,160,0.18);"></div>
		<div style="position: absolute; bottom: 35%; left: 15%; width: 2px; height: 2px; border-radius: 50%; background: rgba(180,200,160,0.15);"></div>
		<div style="position: absolute; bottom: 30%; right: 20%; width: 2px; height: 2px; border-radius: 50%; background: rgba(180,200,160,0.20);"></div>
		<div style="position: absolute; top: 18%; left: 72%; width: 1px; height: 1px; border-radius: 50%; background: rgba(180,200,160,0.14);"></div>
		<div style="position: absolute; top: 40%; left: 30%; width: 1px; height: 1px; border-radius: 50%; background: rgba(180,200,160,0.12);"></div>
	</div>
	<!-- bottom vines -->
	<div class="pointer-events-none absolute z-10" style="bottom: 0; left: 0; right: 0; height: 20px;">
		<!-- base vine -->
		<div style="position: absolute; bottom: 0; left: 0; right: 0; height: 2px; background: #2a3d18;"></div>
		<!-- stem 1 -->
		<div style="position: absolute; bottom: 0; left: 4px; width: 2px; height: 14px; background: #2a3d18;"></div>
		<div style="position: absolute; bottom: 8px; left: 5px; width: 7px; height: 4px; background: #3a5222; border-radius: 0 var(--frame-radius) var(--frame-radius) 0;"></div>
		<!-- stem 2 -->
		<div style="position: absolute; bottom: 0; left: 14px; width: 2px; height: 10px; background: #2a3d18;"></div>
		<div style="position: absolute; bottom: 5px; left: 8px; width: 7px; height: 4px; background: #3a5222; border-radius: var(--frame-radius) 0 0 var(--frame-radius);"></div>
		<!-- stem 3 (tall) -->
		<div style="position: absolute; bottom: 0; left: 24px; width: 2px; height: 19px; background: #2a3d18;"></div>
		<div style="position: absolute; bottom: 11px; left: 25px; width: 7px; height: 4px; background: #4a6a28; border-radius: 0 var(--frame-radius) var(--frame-radius) 0;"></div>
		<div style="position: absolute; bottom: 17px; left: 18px; width: 7px; height: 4px; background: #3a5222; border-radius: var(--frame-radius) 0 0 var(--frame-radius);"></div>
		<!-- stem 4 -->
		<div style="position: absolute; bottom: 0; left: 36px; width: 2px; height: 12px; background: #2a3d18;"></div>
		<div style="position: absolute; bottom: 6px; left: 37px; width: 7px; height: 4px; background: #3a5222; border-radius: 0 var(--frame-radius) var(--frame-radius) 0;"></div>
		<!-- stem 5 (tall) -->
		<div style="position: absolute; bottom: 0; left: 48px; width: 2px; height: 16px; background: #2a3d18;"></div>
		<div style="position: absolute; bottom: 9px; left: 42px; width: 7px; height: 4px; background: #4a6a28; border-radius: var(--frame-radius) 0 0 var(--frame-radius);"></div>
		<div style="position: absolute; bottom: 14px; left: 49px; width: 6px; height: 4px; background: #3a5222; border-radius: 0 var(--frame-radius) var(--frame-radius) 0;"></div>
		<!-- stem 6 -->
		<div style="position: absolute; bottom: 0; left: 58px; width: 2px; height: 9px; background: #2a3d18;"></div>
		<div style="position: absolute; bottom: 4px; left: 52px; width: 7px; height: 4px; background: #3a5222; border-radius: var(--frame-radius) 0 0 var(--frame-radius);"></div>
	</div>
	<!-- left side tendrils -->
	<div class="pointer-events-none absolute z-10" style="top: 0; bottom: 20px; left: 0; width: 14px;">
		<div style="position: absolute; top: 0; left: 0; bottom: 0; width: 2px; background: #2a3d18;"></div>
		<div style="position: absolute; top: 20%; left: 0; width: 8px; height: 4px; background: #3a5222; border-radius: 0 var(--frame-radius) var(--frame-radius) 0;"></div>
		<div style="position: absolute; top: 38%; left: 2px; width: 7px; height: 4px; background: #4a6a28; border-radius: 0 var(--frame-radius) var(--frame-radius) 0;"></div>
		<div style="position: absolute; top: 55%; left: 0; width: 9px; height: 4px; background: #3a5222; border-radius: 0 var(--frame-radius) var(--frame-radius) 0;"></div>
		<div style="position: absolute; top: 72%; left: 2px; width: 6px; height: 4px; background: #4a6a28; border-radius: 0 var(--frame-radius) var(--frame-radius) 0;"></div>
	</div>
	<!-- right side tendrils -->
	<div class="pointer-events-none absolute z-10" style="top: 0; bottom: 20px; right: 0; width: 14px;">
		<div style="position: absolute; top: 0; right: 0; bottom: 0; width: 2px; background: #2a3d18;"></div>
		<div style="position: absolute; top: 28%; right: 0; width: 8px; height: 4px; background: #3a5222; border-radius: var(--frame-radius) 0 0 var(--frame-radius);"></div>
		<div style="position: absolute; top: 46%; right: 2px; width: 7px; height: 4px; background: #4a6a28; border-radius: var(--frame-radius) 0 0 var(--frame-radius);"></div>
		<div style="position: absolute; top: 62%; right: 0; width: 9px; height: 4px; background: #3a5222; border-radius: var(--frame-radius) 0 0 var(--frame-radius);"></div>
		<div style="position: absolute; top: 78%; right: 2px; width: 6px; height: 4px; background: #4a6a28; border-radius: var(--frame-radius) 0 0 var(--frame-radius);"></div>
	</div>
	</div>
	{:else if currentTheme === 'outbreak'}
	<div class="pointer-events-none absolute z-10" style="bottom: 0; left: 0; right: 0; top: 0;">
		<!-- horizontal bars -->
		<div style="position: absolute; top: 0; left: 0; right: 0; height: 3px; background: #2a2820;"></div>
		<div style="position: absolute; bottom: 0; left: 0; right: 0; height: 3px; background: #2a2820;"></div>
		<!-- vertical bars -->
		<div style="position: absolute; top: 0; bottom: 0; left: 10%; width: 4px; background: linear-gradient(to right, #1a1810 0px, #1a1810 1px, #4a4438 1px, #4a4438 3px, #1a1810 3px, #1a1810 4px); border-radius: calc(var(--frame-radius) / 4);"></div>
		<div style="position: absolute; top: 0; bottom: 0; left: 28%; width: 4px; background: linear-gradient(to right, #1a1810 0px, #1a1810 1px, #4a4438 1px, #4a4438 3px, #1a1810 3px, #1a1810 4px); border-radius: calc(var(--frame-radius) / 4);"></div>
<div style="position: absolute; top: 0; bottom: 0; left: 64%; width: 4px; background: linear-gradient(to right, #1a1810 0px, #1a1810 1px, #4a4438 1px, #4a4438 3px, #1a1810 3px, #1a1810 4px); border-radius: calc(var(--frame-radius) / 4);"></div>
		<div style="position: absolute; top: 0; bottom: 0; left: 82%; width: 4px; background: linear-gradient(to right, #1a1810 0px, #1a1810 1px, #4a4438 1px, #4a4438 3px, #1a1810 3px, #1a1810 4px); border-radius: calc(var(--frame-radius) / 4);"></div>
		<!-- rust streaks on a couple bars -->
		<div style="position: absolute; top: 30%; left: calc(28% + 1px); width: 2px; height: 18%; background: linear-gradient(to bottom, transparent, #7a3e10, transparent); opacity: 0.6;"></div>
		<div style="position: absolute; top: 55%; left: calc(64% + 1px); width: 2px; height: 14%; background: linear-gradient(to bottom, transparent, #7a3e10, transparent); opacity: 0.5;"></div>
	</div>
	{:else if currentTheme === 'galactic'}
	<div class="pointer-events-none absolute z-10" style="bottom: 0; left: 0; right: 0; height: 14px;">
		<!-- blade glow body -->
		<div style="position: absolute; bottom: 4px; left: 6px; right: 18px; height: 4px; background: #ff3322; box-shadow: 0 0 4px #fff, 0 0 10px #ff5544, 0 0 22px #ff2200, 0 0 40px #cc1100;"></div>
		<!-- blade highlight stripe -->
		<div style="position: absolute; bottom: 6px; left: 6px; right: 18px; height: 1px; background: #ffaaaa;"></div>
		<!-- blade tip (triangular point left) -->
		<div style="position: absolute; bottom: 4px; left: 0; width: 6px; height: 4px; background: #ff3322; clip-path: polygon(100% 0, 100% 100%, 0 50%); box-shadow: 0 0 6px #ff3322;"></div>
		<!-- cross-guard vertical (chrome) -->
		<div style="position: absolute; bottom: 1px; right: 14px; width: 3px; height: 10px; background: linear-gradient(to right, #aaa, #ddd, #777);"></div>
		<!-- cross-guard horizontal (chrome) -->
		<div style="position: absolute; bottom: 0; right: 12px; width: 7px; height: 2px; background: linear-gradient(to bottom, #ccc, #555);"></div>
		<!-- hilt body (gunmetal) -->
		<div style="position: absolute; bottom: 4px; right: 4px; width: 10px; height: 4px; background: linear-gradient(to bottom, #444 0px, #444 1px, #222 1px, #222 3px, #555 3px); border-top: 1px solid #888;"></div>
		<!-- glowing power core dot -->
		<div style="position: absolute; bottom: 5px; right: 7px; width: 2px; height: 2px; background: #ff3322; box-shadow: 0 0 4px #ff3322, 0 0 8px #cc1100;"></div>
		<!-- pommel cap (chrome) -->
		<div style="position: absolute; bottom: 3px; right: 0; width: 4px; height: 6px; background: linear-gradient(to right, #aaa, #ddd, #555);"></div>
	</div>
	{:else if currentTheme === 'wizardry'}
	<div class="pointer-events-none absolute z-10" style="bottom: 0; left: 0; right: 0; height: 14px;">
		<!-- crystal gem at tip (faceted, glowing purple) -->
		<div style="position: absolute; bottom: 3px; left: 0; width: 7px; height: 8px; background: #c084fc; clip-path: polygon(0 50%, 30% 0, 70% 0, 100% 50%, 70% 100%, 30% 100%); box-shadow: 0 0 6px #c084fc, 0 0 14px #a855f7, 0 0 28px #7e22ce;"></div>
		<!-- gem inner highlight -->
		<div style="position: absolute; bottom: 6px; left: 2px; width: 2px; height: 2px; background: #ffffff; opacity: 0.9;"></div>
		<!-- straight wand shaft (dark wood) -->
		<div style="position: absolute; bottom: 6px; left: 7px; right: 8px; height: 2px; background: linear-gradient(to bottom, #5a3a20 0px, #3a2010 1px, #2a1808 2px);"></div>
		<!-- shaft top highlight -->
		<div style="position: absolute; bottom: 8px; left: 7px; right: 8px; height: 1px; background: #7a4a28;"></div>
		<!-- grip wrap (darker band) -->
		<div style="position: absolute; bottom: 5px; right: 4px; width: 6px; height: 4px; background: #1a1008; border-top: 1px solid #4a2a18; border-bottom: 1px solid #0a0500;"></div>
		<!-- pommel cap (gold ring) -->
		<div style="position: absolute; bottom: 4px; right: 0; width: 4px; height: 6px; background: linear-gradient(to right, #b8860b, #d4af37, #8b5a0a);"></div>
	</div>
	{/if}
	<div class="dark-body" aria-hidden="true"><div class="dark-body-stripes"></div></div>
	<div class="pixel-frame absolute inset-0 bg-(--logo-frame-outer)"></div>
	<div class="pixel-frame absolute inset-0.5 bg-(--logo-frame-highlight)"></div>
	<div class="pixel-frame absolute inset-1 bg-(--logo-frame-shadow)"></div>
	{#if currentTheme === 'wizardry'}
		<div class="pixel-frame absolute inset-1.5" style="background: linear-gradient(135deg, #740001 0%, #740001 25%, #1a472a 25%, #1a472a 50%, #ecb939 50%, #ecb939 75%, #0e1a40 75%, #0e1a40 100%);"></div>
	{:else if currentTheme === 'catchers'}
		<div class="pixel-frame absolute inset-1.5" style="background: linear-gradient(to bottom, #ee1515 0%, #ee1515 50%, #f0f0f0 50%, #f0f0f0 100%);"></div>
	{:else if currentTheme === 'noir'}
		<!-- Newsprint paper body with faint horizontal column-rule lines -->
		<div class="pixel-frame absolute inset-1.5" style="background-color: #b8b8b8; background-image: repeating-linear-gradient(to bottom, transparent 0 4px, rgba(0,0,0,0.10) 4px 5px);"></div>
	{:else}
		<div class="pixel-frame absolute inset-1.5 bg-(--logo-frame-fill)"></div>
	{/if}
	<div class="relative flex h-full w-full items-center justify-center">
		<span class="font-bold" style:font-family="var(--font-sans)" style:font-style="normal" style:font-size="{Math.round(size * 0.72)}px" style:color={currentTheme === 'wizardry' || currentTheme === 'catchers' ? '#ffffff' : 'var(--logo-frame-text)'} style:text-shadow={currentTheme === 'wizardry' || currentTheme === 'catchers' ? '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000' : 'none'}>B</span>
	</div>
	{#if showBeta}
	<div class="pixel-frame absolute z-20" style="bottom: -6px; left: 50%; transform: translateX(-50%);">
		<div class="pixel-frame absolute inset-0 bg-(--correct-frame-outer)"></div>
		<div class="pixel-frame absolute inset-0.5 bg-(--correct-frame-highlight)"></div>
		<div class="pixel-frame absolute inset-1 bg-(--correct-frame-shadow)"></div>
		<div class="pixel-frame absolute inset-1.5 bg-(--correct-frame-fill)"></div>
		<span class="relative block px-2.5 py-1 font-bold uppercase text-(--correct-frame-text)" style:font-size="{Math.round(size * 0.2)}px" style:letter-spacing="0.05em">BETA</span>
	</div>
	{/if}
</div>
</div>

<style>
	/* Flat (dark/light) LogoIcon — the notepad-with-spine look. Scoped to the
	   .logo-icon-dark / .logo-icon-light class set by this component when its
	   currentTheme is dark/light, so theme-picker thumbnails render the correct
	   per-tile logo even when the page's global data-theme differs from the
	   tile's theme. */
	:global(.logo-icon-dark > .pixel-frame:not(.z-20)),
	:global(.logo-icon-dark > .pointer-events-none),
	:global(.logo-icon-dark > .absolute:not(.z-20)),
	:global(.logo-icon-light > .pixel-frame:not(.z-20)),
	:global(.logo-icon-light > .pointer-events-none),
	:global(.logo-icon-light > .absolute:not(.z-20)) {
		display: none !important;
	}
	:global(.logo-icon-dark),
	:global(.logo-icon-light) {
		background: transparent !important;
		overflow: visible !important;
		padding-left: 8px !important;
		box-sizing: border-box !important;
	}
	:global(.logo-icon-dark::before),
	:global(.logo-icon-light::before) {
		content: '';
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		width: 8px;
		background: linear-gradient(to right, #2a2a2a 0%, #181818 40%, #050505 100%);
		border-radius: 22% 0 0 22% / 12% 0 0 12%;
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
		z-index: 5;
		pointer-events: none;
	}
	:global(.logo-icon-dark .dark-body),
	:global(.logo-icon-light .dark-body) {
		display: block !important;
		position: absolute;
		top: 0;
		bottom: 0;
		left: 8px;
		right: 0;
		overflow: hidden;
		border-radius: 0 14% 14% 0 / 0 12% 12% 0;
		z-index: 0;
		pointer-events: none;
	}
	:global(.logo-icon-dark .dark-body-stripes),
	:global(.logo-icon-light .dark-body-stripes) {
		position: absolute;
		inset: 0;
		background:
			linear-gradient(to right, transparent 0 5px, #ef4444 5px 6px, transparent 6px),
			repeating-linear-gradient(to bottom, #ffffff 0 5px, var(--logo-stripe-blue) 5px 6px);
	}
	:global(.dark-body) {
		display: none;
	}
	/* Restore the per-layer pixel-frame insets inside non-flat LogoIcons,
	   counteracting flat theme's "flatten everything" rules when a non-flat
	   thumbnail is rendered on a dark/light page. */
	:global(.logo-icon:not(.logo-icon-dark):not(.logo-icon-light) .absolute[class*='pixel-frame'][class*='-highlight)']) {
		display: block !important;
		inset: 2px !important;
	}
	:global(.logo-icon:not(.logo-icon-dark):not(.logo-icon-light) .absolute[class*='pixel-frame'][class*='-shadow)']) {
		display: block !important;
		inset: 4px !important;
	}
	:global(.logo-icon:not(.logo-icon-dark):not(.logo-icon-light) .absolute[class*='pixel-frame'][class*='-fill)']) {
		inset: 6px !important;
		border: 0 !important;
		box-shadow: none !important;
	}
	/* Hearth uses the default stepped pixel-frame polygon — bound to this
	   LogoIcon's own currentTheme class, not the page-level data-theme cascade. */
	:global(.logo-icon.logo-icon-hearth .pixel-frame) {
		clip-path: polygon(
			0 4px,
			2px 4px,
			2px 2px,
			4px 2px,
			4px 0,
			calc(100% - 4px) 0,
			calc(100% - 4px) 2px,
			calc(100% - 2px) 2px,
			calc(100% - 2px) 4px,
			100% 4px,
			100% calc(100% - 4px),
			calc(100% - 2px) calc(100% - 4px),
			calc(100% - 2px) calc(100% - 2px),
			calc(100% - 4px) calc(100% - 2px),
			calc(100% - 4px) 100%,
			4px 100%,
			4px calc(100% - 2px),
			2px calc(100% - 2px),
			2px calc(100% - 4px),
			0 calc(100% - 4px)
		) !important;
		border-radius: 0 !important;
	}
	@keyframes -global-logo-stripes {
		from {
			transform: translateX(0);
		}
		to {
			transform: translateX(-32px);
		}
	}
</style>

